import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet, Landmark, Home, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle,
  BookOpen, ChevronDown, ChevronUp, ShieldAlert, Save, Info, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaInput from "@/components/jea/JeaInput";
import JeaSelect from "@/components/jea/JeaSelect";
import MoneyInput from "@/components/jea/MoneyInput";
import PillButton from "@/components/jea/PillButton";
import Skeleton from "@/components/jea/Skeleton";
import EmptyState from "@/components/jea/EmptyState";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { formatMoney } from "@/lib/pricing";
import { usePreviewGate } from "@/hooks/usePreviewGate";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { cn } from "@/lib/utils";

const PATHWAYS = [
  {
    key: "wage_garnishment",
    label: "Wage Garnishment",
    Icon: Wallet,
    body: "Divert a portion of the debtor's paycheck directly from their employer under state and federal wage garnishment laws.",
    subnote: "Requires verified employer (see Discovery > Work Number).",
  },
  {
    key: "bank_garnishment",
    label: "Bank Account Garnishment",
    Icon: Landmark,
    body: "Levy the debtor's bank accounts to recover judgment funds. Subject to federal and state exemption limits.",
    subnote: "Requires known bank account (see Discovery > TLOxp/Accurint).",
  },
  {
    key: "property_levy",
    label: "Property Levy",
    Icon: Home,
    body: "Seize personal property to satisfy the judgment. Effective as a negotiation tool even when direct levy is impractical.",
    subnote: "Requires titled personal property (see Discovery > Accurint).",
  },
];


const PathwayCard = ({ p, active, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(p.key)}
    className={cn(
      "text-left w-full rounded-[12px] border p-8 transition-all",
      active
        ? "border-2 border-accent-pink bg-accent-pink/10"
        : "border border-border-default bg-bg-secondary hover:border-accent-pink/40"
    )}
    data-testid={`pathway-${p.key}`}
  >
    <p.Icon size={40} className={active ? "text-accent-pink" : "text-accent-pink"} />
    <div className="mt-4 text-white font-bold text-[18px]">{p.label}</div>
    <p className="text-text-secondary text-[14px] mt-2">{p.body}</p>
    <p className="text-text-muted text-[12px] italic mt-3">{p.subnote}</p>
    <div className="mt-4 inline-flex items-center gap-1 text-accent-pink text-[13px] font-semibold">
      Select This Path <ArrowRight size={14} />
    </div>
  </button>
);


const Step = ({ n, title, testid, children }) => (
  <JeaCard data-testid={testid}>
    <div className="jea-caption text-accent-pink">STEP {n}</div>
    <h3 className="text-white font-bold text-[16px] mt-1 mb-4">{title}</h3>
    {children}
  </JeaCard>
);


// ---------- Wage Garnishment Flow ----------
const WageFlow = ({ debtor, rules, plan, setPlan, onSave, saving, onRunWorkNumber, previewDispatch, canSave }) => {
  const emp = debtor?.employment?.employer_name || debtor?.employer || null;
  const empAddr = debtor?.employment?.employer_address || debtor?.employer_address || "";
  const wg = rules?.wage_garnishment || {};
  const state = (debtor?.judgment?.state || "").toUpperCase();

  // Step 2 compute widget: post-tax estimate ≈ gross × 0.75 (approx take-home)
  const gross = plan.step2?.weekly_gross_cents ?? 0;
  const postTax = Math.round(gross * 0.75);
  const federalFloorCents = wg.min_multiplier_min_wage
    ? Math.round(wg.min_multiplier_min_wage * 7.25 * 100)  // fed min $7.25/hr
    : 21750;  // fed baseline weekly floor $217.50
  const percentCap = Math.round(postTax * ((wg.max_percent_disposable || 25) / 100));
  const disposableOver = Math.max(0, postTax - federalFloorCents);
  const maxGarnish = Math.min(percentCap, disposableOver);

  const setStep = (key, val) => setPlan((p) => ({ ...p, [key]: { ...(p[key] || {}), ...val } }));

  return (
    <div className="space-y-4">
      <Step n={1} title="Confirm Employer" testid="wage-step-1">
        {emp ? (
          <div className="rounded-[8px] bg-bg-primary border border-border-default p-4" data-testid="wage-employer-row">
            <div className="text-text-secondary text-[11px] tracking-wider uppercase">Current Employer</div>
            <div className="text-white font-bold text-[15px] mt-1">{emp}</div>
            <div className="text-text-secondary text-[13px]">{empAddr || "—"}</div>
          </div>
        ) : (
          <div className="rounded-[8px] border-l-4 border-warning bg-warning/5 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-warning shrink-0 mt-0.5" />
              <div className="text-text-secondary text-[13px]">
                No employer on record. Run a Work Number search to verify current employer.
              </div>
            </div>
          </div>
        )}
        <div className="mt-3 flex gap-2 flex-wrap">
          <JeaButton
            size="sm"
            onClick={() => setStep("step1", { confirmed: true })}
            data-testid="wage-step1-confirm"
            variant={plan.step1?.confirmed ? "primary-pink" : "outline-pink"}
          >
            {plan.step1?.confirmed ? "Confirmed ✓" : "Yes, continue →"}
          </JeaButton>
          <JeaButton size="sm" variant="outline-neutral" onClick={onRunWorkNumber} data-testid="wage-step1-work-number">
            Run Work Number verification
          </JeaButton>
        </div>
      </Step>

      <Step n={2} title={`Garnishment Limits — ${state || "State"}`} testid="wage-step-2">
        <p className="text-text-secondary text-[13px] leading-6">
          <strong className="text-white">Cap:</strong> {wg.max_percent_disposable}% of disposable earnings
          {" "}or the amount by which weekly disposable exceeds {wg.min_multiplier_min_wage}× federal minimum wage,
          whichever is less. Priority order: child support &gt; tax &gt; general.
        </p>
        <div className="mt-2 text-text-secondary text-[11px] jea-mono">Statute: {wg.statute}</div>
        {wg.head_of_household_exempt && (
          <div className="mt-3 rounded-[8px] border-l-4 border-warning bg-warning/5 p-3 text-warning text-[13px]">
            Head-of-household protection applies in this state — may fully exempt wages. See Step 4.
          </div>
        )}
        {wg.max_percent_disposable === 0 && (
          <div className="mt-3 rounded-[8px] border-l-4 border-danger bg-danger/5 p-3 text-danger text-[13px]" data-testid="wage-prohibited-notice">
            This state PROHIBITS wage garnishment for consumer debts. Reconsider using bank garnishment or property levy pathways.
          </div>
        )}

        <div className="mt-5 rounded-[8px] bg-bg-primary border border-border-default p-4">
          <div className="jea-caption mb-2">Estimated Weekly Take</div>
          <MoneyInput
            valueCents={gross}
            onChange={(c) => setStep("step2", { weekly_gross_cents: c })}
            placeholder="Debtor's weekly gross"
            testid="wage-weekly-gross"
          />
          {gross > 0 && (
            <div className="mt-3 space-y-1 text-[13px]" data-testid="wage-compute">
              <Row label="Est. Post-tax (~75%)" value={formatMoney(postTax)} />
              <Row label={`${wg.max_percent_disposable}% of Disposable`} value={formatMoney(percentCap)} />
              <Row label="Amount over federal floor" value={formatMoney(disposableOver)} />
              <div className="pt-2 mt-2 border-t border-border-default">
                <Row label="Estimated Max Garnishment / Week"
                     value={formatMoney(maxGarnish)}
                     accent />
              </div>
            </div>
          )}
        </div>
      </Step>

      <Step n={3} title="File the Writ of Garnishment" testid="wage-step-3">
        <ul className="space-y-2 text-text-secondary text-[13px]">
          <li><strong className="text-white">Court:</strong> {debtor?.judgment?.court_name || "—"}</li>
          <li><strong className="text-white">Filing fee:</strong> Typically $30–$60 (varies by court)</li>
          <li><strong className="text-white">Documents:</strong> certified copy of judgment, writ form, affidavit of debt</li>
          <li><strong className="text-white">Service on employer:</strong> personal service or certified mail per state rule</li>
          <li><strong className="text-white">Answer period:</strong> employer has ~20 days to respond</li>
        </ul>
        <JeaButton
          size="sm" variant="outline-neutral" className="mt-4"
          onClick={() => toast.info("Writ form download coming soon.")}
          data-testid="wage-step3-download-writ"
        >
          Download Writ Form (Coming Soon)
        </JeaButton>
      </Step>

      <Step n={4} title="Exemption Check" testid="wage-step-4">
        <p className="text-text-secondary text-[13px] leading-6">
          Federal law protects the first {wg.min_multiplier_min_wage}× federal minimum wage per week
          from garnishment. State law may protect more.
        </p>

        <div className="mt-4 rounded-[8px] bg-bg-primary border border-border-default p-4">
          <div className="text-white font-semibold text-[14px]">
            Is the debtor the primary earner supporting dependent children?
          </div>
          <div className="mt-2 flex gap-2">
            <PillButton
              active={plan.step4?.head_of_household === true}
              onClick={() => setStep("step4", { head_of_household: true })}
              data-testid="wage-hoh-yes"
            >
              Yes
            </PillButton>
            <PillButton
              active={plan.step4?.head_of_household === false}
              onClick={() => setStep("step4", { head_of_household: false })}
              data-testid="wage-hoh-no"
            >
              No
            </PillButton>
          </div>
          {plan.step4?.head_of_household === true && wg.head_of_household_exempt && (
            <div className="mt-3 rounded-[8px] border-l-4 border-warning bg-warning/5 p-3 text-warning text-[13px]"
                 data-testid="wage-hoh-warning">
              In this state, head-of-household wages may be fully exempt from garnishment.
              Consider whether wage garnishment is the right pathway. Consult local counsel.
              Statute reference: <span className="jea-mono">{wg.statute}</span>
            </div>
          )}
        </div>

        <div className="mt-4 text-text-secondary text-[13px]">
          <strong className="text-white">Federal exemptions:</strong> Social Security, disability,
          veterans benefits, and other federal benefits deposited via direct deposit are exempt
          from garnishment (31 U.S.C. § 3716).
        </div>
      </Step>

      <div className="flex items-center gap-2 justify-end">
        {canSave && (
          <JeaButton
            variant="outline-pink"
            onClick={onSave}
            loading={saving}
            className="gap-2"
            data-testid="enforcement-save-plan"
          >
            <Save size={14} /> Save Enforcement Plan
          </JeaButton>
        )}
      </div>
    </div>
  );
};


// ---------- Bank Garnishment Flow ----------
const BankFlow = ({ debtor, rules, plan, setPlan, onSave, saving, onRunAccurint, canSave }) => {
  const banks = debtor?.known_bank_accounts || [];
  const bg = rules?.bank_garnishment || {};
  const setStep = (key, val) => setPlan((p) => ({ ...p, [key]: { ...(p[key] || {}), ...val } }));

  return (
    <div className="space-y-4">
      <Step n={1} title="Known Bank Accounts" testid="bank-step-1">
        {banks.length ? (
          <div className="space-y-2">
            {banks.map((b, i) => (
              <div key={i} className="rounded-[8px] bg-bg-primary border border-border-default p-3">
                <div className="text-white font-semibold text-[14px]">{b.institution || b.bank || "Unknown Institution"}</div>
                <div className="text-text-secondary text-[12px] jea-mono">
                  ****{b.last4 || b.account_last4 || "----"}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-[8px] border-l-4 border-warning bg-warning/5 p-4" data-testid="bank-no-accounts">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-warning shrink-0 mt-0.5" />
              <div className="text-text-secondary text-[13px]">
                No known bank accounts. Run Discovery (Accurint / TLOxp) to surface accounts before proceeding.
              </div>
            </div>
            <JeaButton size="sm" variant="outline-pink" className="mt-3" onClick={onRunAccurint} data-testid="bank-run-discovery">
              Run Discovery Search
            </JeaButton>
          </div>
        )}
      </Step>

      <Step n={2} title="State Bank Garnishment Rules" testid="bank-step-2">
        <ul className="space-y-2 text-text-secondary text-[13px]">
          <li><strong className="text-white">Instrument:</strong> writ of attachment / bank levy / execution (state-specific)</li>
          <li><strong className="text-white">Hold period:</strong> bank must hold funds for {bg.hold_period_days || 21} days</li>
          <li><strong className="text-white">Notice:</strong> debtor is entitled to notice + exemption claim window</li>
        </ul>
        <div className="mt-2 text-text-secondary text-[11px] jea-mono">Statute: {bg.statute || "State-specific"}</div>
      </Step>

      <Step n={3} title="Writ Instructions" testid="bank-step-3">
        <ul className="space-y-2 text-text-secondary text-[13px]">
          <li><strong className="text-white">Court:</strong> {debtor?.judgment?.court_name || "—"}</li>
          <li><strong className="text-white">Filing fee:</strong> Typically $30–$100</li>
          <li><strong className="text-white">Documents:</strong> certified judgment, writ form, garnishee statement</li>
          <li><strong className="text-white">Service on bank:</strong> personal service on the branch's authorized agent, or certified mail</li>
        </ul>
        <div className="mt-4 rounded-[8px] border-l-4 border-accent-pink bg-accent-pink/5 p-3 text-text-secondary text-[13px]">
          <strong className="text-white">Federal auto-protection (31 C.F.R. § 212):</strong> banks must automatically
          protect 2 months of Social Security / VA / SSI / federal retirement benefits deposited to
          the account before honoring the writ.
        </div>
      </Step>

      <Step n={4} title="Exemption Check" testid="bank-step-4">
        <div className="space-y-3">
          <YesNoRow
            label="Does the debtor receive Social Security, SSDI, VA, or federal pension deposits?"
            testid="bank-fed-benefits"
            value={plan.step4?.federal_benefits}
            onChange={(v) => setStep("step4", { federal_benefits: v })}
          />
          <YesNoRow
            label="Does the debtor receive state pension deposits?"
            testid="bank-state-pension"
            value={plan.step4?.state_pension}
            onChange={(v) => setStep("step4", { state_pension: v })}
          />
          <YesNoRow
            label="Is the account jointly held with a non-debtor?"
            testid="bank-joint-account"
            value={plan.step4?.joint_account}
            onChange={(v) => setStep("step4", { joint_account: v })}
          />
        </div>

        {(plan.step4?.federal_benefits === true) && (
          <div className="mt-3 rounded-[8px] border-l-4 border-warning bg-warning/5 p-3 text-warning text-[13px]"
               data-testid="bank-fed-warning">
            Federal benefits are FULLY PROTECTED. Bank must automatically shield 2 months of deposits (31 C.F.R. § 212).
            Only the excess (if any) is reachable.
          </div>
        )}
        {(plan.step4?.joint_account === true) && (
          <div className="mt-3 rounded-[8px] border-l-4 border-warning bg-warning/5 p-3 text-warning text-[13px]"
               data-testid="bank-joint-warning">
            Joint account rules vary by state. Some states protect the entire account if one holder is a non-debtor.
            Others allow levy proportional to debtor's contribution. Consult local counsel.
          </div>
        )}
      </Step>

      <div className="flex items-center gap-2 justify-end">
        {canSave && (
          <JeaButton
            variant="outline-pink"
            onClick={onSave}
            loading={saving}
            className="gap-2"
            data-testid="enforcement-save-plan"
          >
            <Save size={14} /> Save Enforcement Plan
          </JeaButton>
        )}
      </div>
    </div>
  );
};


// ---------- Property Levy Flow ----------
const PropertyFlow = ({ debtor, rules, plan, setPlan, onSave, saving, canSave }) => {
  const rp = rules?.personal_property || {};
  const setStep = (key, val) => setPlan((p) => ({ ...p, [key]: { ...(p[key] || {}), ...val } }));

  const vehicles = debtor?.consolidated_assets?.vehicles || debtor?.vehicles || [];
  const realProperty = debtor?.consolidated_assets?.real_property || debtor?.real_property || [];
  const items = [
    ...realProperty.map((p) => ({ type: "Real Property", label: p.address || p.description, value_cents: p.estimated_value_cents || 0 })),
    ...vehicles.map((v) => ({ type: "Vehicle", label: `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim() || "Vehicle", value_cents: v.estimated_value_cents || 0 })),
  ].filter((x) => x.label);
  const top3 = [...items].sort((a, b) => b.value_cents - a.value_cents).slice(0, 3);

  const netEquity = plan.step2?.net_equity_cents ?? 0;

  return (
    <div className="space-y-4">
      <Step n={1} title="Known Personal Property" testid="prop-step-1">
        {items.length === 0 ? (
          <div className="rounded-[8px] border-l-4 border-warning bg-warning/5 p-4">
            <div className="text-text-secondary text-[13px]">
              No known assets. Run Discovery (Accurint) to surface real property + vehicles.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="prop-items-list">
            {items.map((it, i) => (
              <div key={i}
                   className={cn(
                     "flex items-center justify-between rounded-[8px] bg-bg-primary border p-3",
                     top3.includes(it) ? "border-accent-pink/40" : "border-border-default"
                   )}
              >
                <div>
                  <div className="text-white font-semibold text-[14px]">{it.label}</div>
                  <div className="text-text-secondary text-[12px]">{it.type}</div>
                </div>
                <div className="jea-mono text-accent-pink font-bold text-[14px]">{formatMoney(it.value_cents)}</div>
              </div>
            ))}
          </div>
        )}
      </Step>

      <Step n={2} title="Threshold Questions" testid="prop-step-2">
        <YesNoUnknownRow
          label="Is the asset titled solely in the debtor's name?"
          testid="prop-titled-solely"
          value={plan.step2?.titled_solely}
          onChange={(v) => setStep("step2", { titled_solely: v })}
        />
        <YesNoUnknownRow
          label="Are there any prior liens against the asset?"
          testid="prop-prior-liens"
          value={plan.step2?.prior_liens}
          onChange={(v) => setStep("step2", { prior_liens: v })}
        />
        <div className="mt-4">
          <div className="text-white text-[14px] mb-1.5">What is the net equity after encumbrances?</div>
          <MoneyInput
            valueCents={netEquity}
            onChange={(c) => setStep("step2", { net_equity_cents: c })}
            testid="prop-net-equity"
          />
        </div>
        <div className="mt-3 pt-3 border-t border-border-default flex items-center justify-between">
          <span className="text-white font-semibold text-[14px]">Estimated Net Recovery</span>
          <span className="jea-mono text-accent-pink font-extrabold text-[18px]" data-testid="prop-net-recovery">
            {formatMoney(netEquity)}
          </span>
        </div>
      </Step>

      <Step n={3} title="State Personal Property Exemptions" testid="prop-step-3">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="prop-exemptions-table">
            <thead>
              <tr className="border-b border-border-default">
                <th className="jea-caption text-text-secondary p-2 text-left">Category</th>
                <th className="jea-caption text-text-secondary p-2 text-right">Exemption</th>
                <th className="jea-caption text-text-secondary p-2 text-left">Statute</th>
              </tr>
            </thead>
            <tbody>
              <ExRow label="Homestead" value={rp.homestead_cents} unlimited statute={rp.statute} />
              <ExRow label="Motor Vehicle" value={rp.motor_vehicle_cents} statute={rp.statute} />
              <ExRow label="Tools of Trade" value={rp.tools_of_trade_cents} statute={rp.statute} />
              <ExRow label="Household Goods" value={rp.household_goods_cents} statute={rp.statute} />
              <tr className="border-b border-border-default">
                <td className="p-2 text-white">Retirement Accounts</td>
                <td className="p-2 text-right text-success font-bold">
                  {rp.retirement_exempt ? "Fully Exempt" : "See statute"}
                </td>
                <td className="p-2 jea-mono text-text-secondary text-[11px]">{rp.statute || "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Step>

      <Step n={4} title="Strategic Recommendation" testid="prop-step-4">
        <div className="rounded-[8px] border-l-4 border-accent-pink bg-accent-pink/5 p-4">
          <div className="flex items-start gap-3">
            <ShieldAlert size={20} className="text-accent-pink shrink-0 mt-1" />
            <div className="text-text-secondary text-[13px] leading-6">
              Personal property levy is often more valuable as a <strong className="text-white">negotiating tool</strong>
              than actual levy execution. Consider serving a Notice of Levy first to encourage
              voluntary payment before proceeding to physical seizure.
              {top3[0] && (
                <div className="mt-2">
                  Highest-value target: <span className="jea-mono text-accent-pink">{formatMoney(top3[0].value_cents)}</span> — <em>{top3[0].label}</em>.
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
          <JeaButton size="sm" variant="outline-pink" onClick={() => toast.info("Notice draft coming soon.")} data-testid="prop-serve-notice">
            Serve Notice of Levy
          </JeaButton>
          <JeaButton size="sm" variant="outline-neutral" onClick={() => toast.info("Physical levy checklist coming soon.")} data-testid="prop-physical-levy">
            Proceed to Physical Levy
          </JeaButton>
        </div>
      </Step>

      <div className="flex items-center gap-2 justify-end">
        {canSave && (
          <JeaButton
            variant="outline-pink"
            onClick={onSave}
            loading={saving}
            className="gap-2"
            data-testid="enforcement-save-plan"
          >
            <Save size={14} /> Save Enforcement Plan
          </JeaButton>
        )}
      </div>
    </div>
  );
};


const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between">
    <span className={accent ? "text-white font-bold text-[14px]" : "text-text-secondary"}>{label}</span>
    <span className={cn(
      "jea-mono font-semibold",
      accent ? "text-accent-pink text-[18px] font-extrabold" : "text-white text-[14px]"
    )}>
      {value}
    </span>
  </div>
);

const YesNoRow = ({ label, value, onChange, testid }) => (
  <div className="rounded-[8px] bg-bg-primary border border-border-default p-3" data-testid={testid}>
    <div className="text-white text-[14px] mb-2">{label}</div>
    <div className="flex gap-2">
      <PillButton active={value === true} onClick={() => onChange(true)} data-testid={`${testid}-yes`}>Yes</PillButton>
      <PillButton active={value === false} onClick={() => onChange(false)} data-testid={`${testid}-no`}>No</PillButton>
    </div>
  </div>
);

const YesNoUnknownRow = ({ label, value, onChange, testid }) => (
  <div className="rounded-[8px] bg-bg-primary border border-border-default p-3 mt-3" data-testid={testid}>
    <div className="text-white text-[14px] mb-2">{label}</div>
    <div className="flex gap-2">
      <PillButton active={value === "yes"} onClick={() => onChange("yes")} data-testid={`${testid}-yes`}>Yes</PillButton>
      <PillButton active={value === "no"} onClick={() => onChange("no")} data-testid={`${testid}-no`}>No</PillButton>
      <PillButton active={value === "unknown"} onClick={() => onChange("unknown")} data-testid={`${testid}-unknown`}>Unknown</PillButton>
    </div>
  </div>
);

const ExRow = ({ label, value, unlimited, statute }) => (
  <tr className="border-b border-border-default">
    <td className="p-2 text-white">{label}</td>
    <td className="p-2 text-right jea-mono">
      {value == null ? <span className="text-success font-bold">Unlimited</span> : (
        value > 0 ? <span className="text-accent-pink font-semibold">{formatMoney(value)}</span> : <span className="text-text-muted">—</span>
      )}
    </td>
    <td className="p-2 jea-mono text-text-secondary text-[11px]">{statute || "—"}</td>
  </tr>
);


// ---------- State Reference Panel ----------
const StateReferencePanel = ({ state, rules }) => {
  const [open, setOpen] = useState(false);
  if (!rules) return null;
  const rp = rules.personal_property || {};
  const wg = rules.wage_garnishment || {};
  const bg = rules.bank_garnishment || {};
  return (
    <JeaCard data-testid="state-reference-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full"
        data-testid="state-reference-toggle"
      >
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-accent-pink" />
          <span className="text-white font-semibold text-[15px]">
            State Exemption Reference — {state || "—"}
          </span>
        </div>
        {open ? <ChevronUp size={16} className="text-text-secondary" /> : <ChevronDown size={16} className="text-text-secondary" />}
      </button>
      {open && (
        <div className="mt-4 space-y-4" data-testid="state-reference-body">
          <p className="text-text-secondary text-[13px]">{rules.notes}</p>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-border-default">
                <th className="jea-caption text-text-secondary p-2 text-left">Category</th>
                <th className="jea-caption text-text-secondary p-2 text-right">Amount / Rule</th>
                <th className="jea-caption text-text-secondary p-2 text-left">Statute</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border-default">
                <td className="p-2 text-white">Wage — Max % Disposable</td>
                <td className="p-2 text-right jea-mono text-accent-pink font-semibold">{wg.max_percent_disposable}%</td>
                <td className="p-2 jea-mono text-text-secondary text-[11px]">{wg.statute}</td>
              </tr>
              <tr className="border-b border-border-default">
                <td className="p-2 text-white">Wage — Min Wage Multiplier</td>
                <td className="p-2 text-right jea-mono text-white font-semibold">{wg.min_multiplier_min_wage}×</td>
                <td className="p-2 jea-mono text-text-secondary text-[11px]">{wg.statute}</td>
              </tr>
              <tr className="border-b border-border-default">
                <td className="p-2 text-white">Head of Household</td>
                <td className="p-2 text-right font-semibold">
                  {wg.head_of_household_exempt ? <span className="text-success">Protected</span> : <span className="text-text-muted">Not protected</span>}
                </td>
                <td className="p-2 jea-mono text-text-secondary text-[11px]">{wg.statute}</td>
              </tr>
              <ExRow label="Homestead" value={rp.homestead_cents} unlimited statute={rp.statute} />
              <ExRow label="Motor Vehicle" value={rp.motor_vehicle_cents} statute={rp.statute} />
              <ExRow label="Tools of Trade" value={rp.tools_of_trade_cents} statute={rp.statute} />
              <ExRow label="Household Goods" value={rp.household_goods_cents} statute={rp.statute} />
              <tr className="border-b border-border-default">
                <td className="p-2 text-white">Retirement Accounts</td>
                <td className="p-2 text-right text-success font-bold">
                  {rp.retirement_exempt ? "Fully Exempt" : "See statute"}
                </td>
                <td className="p-2 jea-mono text-text-secondary text-[11px]">{rp.statute}</td>
              </tr>
              <tr className="border-b border-border-default">
                <td className="p-2 text-white">Bank Hold Period</td>
                <td className="p-2 text-right jea-mono text-accent-pink font-semibold">{bg.hold_period_days} days</td>
                <td className="p-2 jea-mono text-text-secondary text-[11px]">{bg.statute}</td>
              </tr>
              <tr>
                <td className="p-2 text-white">Federal Benefits (SS/VA/SSDI/pension)</td>
                <td className="p-2 text-right text-success font-bold">Fully Exempt</td>
                <td className="p-2 jea-mono text-text-secondary text-[11px]">31 U.S.C. § 3716</td>
              </tr>
            </tbody>
          </table>
          <div className="text-text-muted text-[11px] italic">Not legal advice. Verify current statute values with local counsel.</div>
        </div>
      )}
    </JeaCard>
  );
};


// ---------- Main Page ----------
const EnforcementPage = () => {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { guardAction } = usePreviewGate();
  const { canMutateDebtors } = useRolePermissions();

  const [debtors, setDebtors] = useState([]);
  const [selectedDebtor, setSelectedDebtor] = useState(searchParams.get("debtor_id") || "");
  const [debtorFull, setDebtorFull] = useState(null);
  const [pathway, setPathway] = useState(null);
  const [rules, setRules] = useState(null);
  const [plan, setPlan] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Boot: load debtor list
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/debtors");
        const active = (data.debtors || []).filter((d) => d.status !== "archived");
        setDebtors(active);
        if (!selectedDebtor && active[0]) setSelectedDebtor(active[0].id);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load full debtor + state rules whenever selectedDebtor changes
  useEffect(() => {
    if (!selectedDebtor) return;
    (async () => {
      try {
        const { data } = await apiClient.get(`/debtors/${selectedDebtor}`);
        setDebtorFull(data);
        const state = (data.judgment?.state || "").toUpperCase();
        if (state) {
          const { data: r } = await apiClient.get(`/enforcement/state-rules/${state}`);
          setRules(r);
        }
        setSearchParams((s) => {
          const n = new URLSearchParams(s);
          n.set("debtor_id", selectedDebtor);
          return n;
        });
        setPlan({});
      } catch (e) { /* ok */ }
    })();
  }, [selectedDebtor, setSearchParams]);

  const savePlan = guardAction(async () => {
    if (!pathway || !selectedDebtor) return;
    setSaving(true);
    try {
      await apiClient.post("/enforcement/plans", {
        debtor_id: selectedDebtor,
        pathway,
        plan_data: plan,
      });
      toast.success("Enforcement plan saved.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }, { feature: "saving enforcement plans" });

  const onRunWorkNumber = () => nav(`/discovery?judgment_id=${selectedDebtor}`);
  const onRunAccurint = () => nav(`/discovery?judgment_id=${selectedDebtor}`);

  const state = (debtorFull?.judgment?.state || "").toUpperCase();

  return (
    <div className="space-y-6" data-testid="enforcement-page">
      <div className="space-y-1">
        <div className="jea-caption">Enforcement Tools</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Enforcement Workspace</h1>
      </div>

      {loading ? (
        <Skeleton className="h-24" />
      ) : debtors.length === 0 ? (
        <EmptyState
          icon={Home}
          title="No judgments yet"
          description={canMutateDebtors ? "Add a judgment to begin enforcement planning." : "No judgments are visible for your role."}
          testid="enforcement-empty"
          footer={canMutateDebtors ? <JeaButton onClick={() => nav("/debtors/new")}>Add Judgment</JeaButton> : null}
        />
      ) : (
        <>
          <JeaCard data-testid="enforcement-debtor-picker-card">
            <div className="jea-caption mb-2">Choose a Judgment</div>
            <JeaSelect
              value={selectedDebtor}
              onChange={setSelectedDebtor}
              options={debtors.map((d) => ({
                value: d.id,
                label: `${d.full_name}${d.judgment?.case_number ? ` · ${d.judgment.case_number}` : ""}`,
              }))}
              data-testid="enforcement-debtor-picker"
            />
          </JeaCard>

          {selectedDebtor && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="enforcement-pathways">
                {PATHWAYS.map((p) => (
                  <PathwayCard
                    key={p.key}
                    p={p}
                    active={pathway === p.key}
                    onSelect={setPathway}
                  />
                ))}
              </div>

              {pathway && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setPathway(null)}
                    className="inline-flex items-center gap-1 text-text-secondary hover:text-accent-pink text-[13px] mb-3"
                    data-testid="enforcement-back-pathways"
                  >
                    <ArrowLeft size={14} /> Back to Pathways
                  </button>
                </div>
              )}

              {pathway === "wage_garnishment" && (
                <WageFlow
                  debtor={debtorFull}
                  rules={rules}
                  plan={plan}
                  setPlan={setPlan}
                  onSave={savePlan}
                  saving={saving}
                  onRunWorkNumber={onRunWorkNumber}
                  canSave={canMutateDebtors}
                />
              )}
              {pathway === "bank_garnishment" && (
                <BankFlow
                  debtor={debtorFull}
                  rules={rules}
                  plan={plan}
                  setPlan={setPlan}
                  onSave={savePlan}
                  saving={saving}
                  onRunAccurint={onRunAccurint}
                  canSave={canMutateDebtors}
                />
              )}
              {pathway === "property_levy" && (
                <PropertyFlow
                  debtor={debtorFull}
                  rules={rules}
                  plan={plan}
                  setPlan={setPlan}
                  onSave={savePlan}
                  saving={saving}
                  canSave={canMutateDebtors}
                />
              )}

              <StateReferencePanel state={state} rules={rules} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default EnforcementPage;
